import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Flex, IconButton, Text, useToast,
} from '@chakra-ui/react';
import { LuArchive, LuCheck, LuEllipsis, LuX } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { fetchGroupRows, updateGoal } from '../../api/endpointsBackend';
import { ConvertedAmountsProvider } from '../../contexts/ConvertedAmountsContext';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useGoalsView } from '../../hooks/useGoalsView';
import { formatMonthYear, goalStatusKey, monthsUntil } from '../../utils/goals';
import GoalIcon from '../goals/GoalIcon';
import { GoalBar } from '../goals/GoalProgress';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import { usePhoneShell } from './PhoneShellContext';
import {
  DesktopNoteCard, ListCard, MovementRows, PhoneState, SectionCaption, ToggleRow, money,
} from './PhoneReadOnlyParts';

const PAGE = 15;
const SECTIONS = ['active', 'ready', 'archived'];

// The desktop's status colours, in the phone's contrast-checked tones.
const STATUS_TONE = {
  achieved: 'phone.income',
  onPace: 'phone.income',
  behindPace: 'phone.alert',
  overdue: 'phone.expense',
};

/** "On pace · 9 months left · target June 2027", as the desktop panel says it. */
function StatusLine({ goal, summary, textAlign }) {
  const { t, i18n } = useTranslation('goals');
  const key = goalStatusKey(summary);
  // Mid-sentence, Spanish writes the month in lower case.
  const targetDate = goal.target_date && formatMonthYear(goal.target_date);
  const target = targetDate && i18n.language?.startsWith('es') ? targetDate.toLocaleLowerCase('es') : targetDate;
  const parts = [];
  if (goal.saving_mode === 'target_date' && summary && !summary.is_achieved && !summary.is_overdue) {
    const left = monthsUntil(goal.target_date);
    parts.push(t(left === 1 ? 'status.monthsLeftOne' : 'status.monthsLeftOther', { count: left }));
  }
  if (target) parts.push(t('status.target', { date: target }));
  if (goal.saving_mode === 'monthly_contribution') {
    parts.push(t('status.monthly', { amount: money(goal.currency, goal.monthly_contribution) }));
  }
  return (
    <Text textStyle="phoneMeta" color="phone.muted" textAlign={textAlign} noOfLines={2}>
      {key && <Box as="span" color={STATUS_TONE[key]} fontWeight="600">{t(`status.${key}`)}</Box>}
      {parts.map((p) => <React.Fragment key={p}>{' · '}{p}</React.Fragment>)}
    </Text>
  );
}

function GoalRow({ goal, onClick }) {
  const { t } = useTranslation('goals');
  const summary = goal.summary;
  const currency = summary ? summary.currency : goal.currency;

  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="grid"
      gridTemplateColumns="40px minmax(0, 1fr) auto"
      columnGap="12px"
      rowGap="6px"
      alignItems="center"
      w="100%"
      minH="64px"
      py="10px"
      px="14px"
      textAlign="left"
      opacity={goal.status === 'archived' ? 0.6 : 1}
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <Box gridRow="1 / 3"><GoalIcon goal={goal} size={40} /></Box>
      <Text textStyle="phoneRowName" color="redesign.heading" isTruncated>{goal.name}</Text>
      <Text textStyle="phoneRowAmount" color={(summary?.saved || 0) < 0 ? 'phone.expense' : 'redesign.heading'} textAlign="right" whiteSpace="nowrap">
        {summary ? money(currency, summary.saved) : '—'}
      </Text>
      <GoalBar pct={summary?.pct} color={goal.color} h="4px" />
      <Text textStyle="phoneMeta" color={summary?.is_achieved ? 'phone.income' : 'phone.muted'} textAlign="right" whiteSpace="nowrap">
        {summary?.is_achieved
          ? t('status.achieved')
          : t('phone.ofTarget', { amount: money(null, summary ? summary.target : goal.target_amount) })}
      </Text>
    </Box>
  );
}

/** The ⋯ sheet: the two status changes the phone keeps. */
function GoalActions({ goal, onPick }) {
  const { t } = useTranslation(['goals', 'common']);
  const { close } = usePhoneSheet();
  const actions = [
    goal.status !== 'ready' && { key: 'ready', icon: LuCheck, label: t('actions.markReady') },
    goal.status !== 'archived' && { key: 'archived', icon: LuArchive, label: t('actions.archive') },
  ].filter(Boolean);

  return (
    <>
      <ListCard mt="4px" dividerInset="54px">
        {actions.map((a) => (
          <Flex
            key={a.key}
            as="button"
            type="button"
            align="center"
            gap="12px"
            w="100%"
            minH="52px"
            px="16px"
            color="redesign.heading"
            _active={{ bg: 'redesign.bg.hover' }}
            // Leave this sheet's history entry before changing anything.
            onClick={async () => { await close(); onPick(a.key); }}
          >
            <Box as={a.icon} boxSize="20px" color="phone.muted" />
            <Text textStyle="phoneBody">{a.label}</Text>
          </Flex>
        ))}
      </ListCard>
      <Flex
        as="button"
        type="button"
        align="center"
        justify="center"
        mx="16px"
        mt="8px"
        w="calc(100% - 32px)"
        minH="52px"
        borderRadius="14px"
        bg="redesign.bg.card"
        textStyle="phoneHeadline"
        color="phone.link"
        onClick={() => close()}
      >
        {t('common:actions.cancel')}
      </Flex>
    </>
  );
}

function CloseButton() {
  const { t } = useTranslation('common');
  const { close } = usePhoneSheet();
  return (
    <IconButton
      icon={<LuX size={22} />}
      variant="ghost"
      borderRadius="full"
      color="redesign.text"
      minW="44px"
      minH="44px"
      aria-label={t('actions.close')}
      onClick={() => close()}
    />
  );
}

/**
 * One goal, read-only: what is saved against the target, how it is pacing,
 * and the movements that got it there. ⋯ keeps only "Mark ready to spend"
 * and "Archive"; every other change stays on the computer.
 */
function GoalSheet({ goal, isOpen, onClose, canUpdate, onStatusChange }) {
  const { t } = useTranslation('goals');
  const { openMovement, refreshSignal } = usePhoneShell();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);

  const goalId = goal?.id;
  const load = useCallback(async (next = null) => {
    setLoading(true);
    try {
      const data = await fetchGroupRows({
        filters: [{ entity: 'Goal', column: 'id', operator: 'in', values: [goalId] }],
        orderBy: 'date_desc',
        limit: PAGE,
        cursor: next,
      });
      setRows((prev) => (next ? [...prev, ...(data.items || [])] : (data.items || [])));
      setCursor(data.next);
    } catch {
      if (!next) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [goalId]);

  useEffect(() => {
    if (isOpen && goalId != null) load();
  }, [isOpen, goalId, load, refreshSignal]);

  useEffect(() => {
    if (!isOpen) { setRows([]); setCursor(null); setActionsOpen(false); }
  }, [isOpen]);

  const summary = goal?.summary;
  // Every status leaves at least one of the two changes to offer.
  const hasActions = canUpdate && Boolean(goal);

  return (
    <PhoneSheet
      id="goal"
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={goal?.name}
      headerLeft={<CloseButton />}
      headerRight={hasActions ? (
        <IconButton
          icon={<LuEllipsis size={22} />}
          variant="ghost"
          borderRadius="full"
          color="redesign.text"
          minW="44px"
          minH="44px"
          aria-label={t('detail.options')}
          onClick={() => setActionsOpen(true)}
        />
      ) : null}
    >
      {goal && (
        <>
          <Flex direction="column" align="center" pt="14px" px="16px" textAlign="center">
            <GoalIcon goal={goal} size={72} />
            <Text fontSize="14px" lineHeight="20px" color="phone.muted" mt="12px">{t('detail.saved')}</Text>
            <Text fontSize="28px" lineHeight="34px" fontWeight="700" letterSpacing="-0.02em" color={(summary?.saved || 0) < 0 ? 'phone.expense' : 'redesign.heading'} mt="2px" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {summary ? money(summary.currency, summary.saved) : '—'}
            </Text>
            <Text textStyle="phoneMeta" color="phone.muted" mt="2px">
              {t('phone.ofTargetFunded', {
                amount: money(summary ? summary.currency : goal.currency, summary ? summary.target : goal.target_amount),
                pct: Math.round(summary?.pct || 0),
              })}
            </Text>
          </Flex>

          <Box mx="16px" mt="20px">
            <GoalBar pct={summary?.pct} color={goal.color} h="6px" />
            <Box mt="10px"><StatusLine goal={goal} summary={summary} textAlign="center" /></Box>
            {summary?.approximate && (
              <Text textStyle="phoneMeta" color="phone.muted" textAlign="center" mt="6px">
                {summary.num_unconvertible > 0
                  ? t('approximate', { excluded: summary.num_unconvertible })
                  : t('approximateConverted')}
              </Text>
            )}
          </Box>

          <DesktopNoteCard />

          <SectionCaption>{t('movements.heading')}</SectionCaption>
          <MovementRows
            movements={rows}
            loading={loading && rows.length === 0}
            emptyLabel={t('phone.noMovements')}
            onOpen={openMovement}
            onMore={cursor ? () => load(cursor) : null}
            moreLabel={t('movements.showMore')}
            loadingMore={loading && rows.length > 0}
          />
        </>
      )}

      <PhoneSheet
        id="goal-actions"
        isOpen={actionsOpen}
        onClose={() => setActionsOpen(false)}
        size="content"
      >
        {goal && <GoalActions goal={goal} onPick={(status) => onStatusChange(goal, status)} />}
      </PhoneSheet>
    </PhoneSheet>
  );
}

/**
 * Goals on the phone: active, ready to spend and archived, each with what
 * is saved against its target. A row opens the goal's detail sheet.
 * Creating, editing and attaching stay on the computer.
 */
export default function PhoneGoals() {
  return (
    <ConvertedAmountsProvider>
      <PhoneGoalsList />
    </ConvertedAmountsProvider>
  );
}

function PhoneGoalsList() {
  const { t } = useTranslation('goals');
  const toast = useToast();
  const { displayCurrency } = useDisplayCurrency();
  const { can } = useWorkspace();
  const { refreshSignal } = usePhoneShell();
  const enabled = useGoalsEnabled();
  const [openId, setOpenId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const { rows, loading, error, refetch } = useGoalsView({ currency: displayCurrency, enabled });

  // `?goal_id=<id>` (a goal row on Home, or a link from the desktop) opens that
  // goal's sheet over the list. The id leaves the URL first, so the sheet's
  // history entry sits on plain /goals and a back swipe lands on the list.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('goal_id')) return;
    const id = Number(params.get('goal_id'));
    params.delete('goal_id');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    if (Number.isFinite(id) && id > 0) setOpenId(id);
  }, [location.pathname, location.search, navigate]);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (refreshSignal) refetchRef.current();
  }, [refreshSignal]);

  const openGoal = rows.find((g) => g.id === openId) || null;
  const [lastGoal, setLastGoal] = useState(null);
  useEffect(() => { if (openGoal) setLastGoal(openGoal); }, [openGoal]);

  const handleStatusChange = async (goal, status) => {
    try {
      await updateGoal(goal.id, { status });
      toast({ description: t('toasts.updated', { name: goal.name }), status: 'success', duration: 3000, isClosable: true });
      refetchRef.current();
    } catch {
      toast({ description: t('toasts.saveFailed'), status: 'error', duration: 5000, isClosable: true });
    }
  };

  // Goals is a per-user switch: with it off, the page does not exist for them.
  if (!enabled) return <Navigate to="/dashboard" replace />;

  const sections = SECTIONS
    .map((status) => ({ status, goals: rows.filter((g) => g.status === status) }))
    .filter((s) => s.goals.length > 0);

  let list;
  if (loading && rows.length === 0) list = <PhoneState loading />;
  else if (error) list = <PhoneState>{t('loadError')}</PhoneState>;
  else if (rows.length === 0) list = <PhoneState>{t('empty.title')}</PhoneState>;
  else {
    list = sections.map(({ status, goals }) => {
      if (status === 'archived') {
        return (
          <React.Fragment key={status}>
            <ToggleRow onClick={() => setShowArchived((v) => !v)}>
              {t(showArchived ? 'phone.hideArchived' : 'phone.showArchived', { count: goals.length })}
            </ToggleRow>
            {showArchived && (
              <ListCard mt="4px">
                {goals.map((g) => <GoalRow key={g.id} goal={g} onClick={() => setOpenId(g.id)} />)}
              </ListCard>
            )}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={status}>
          <SectionCaption pt="16px">{`${t(`sections.${status}`)} · ${goals.length}`}</SectionCaption>
          <ListCard mt="0">
            {goals.map((g) => <GoalRow key={g.id} goal={g} onClick={() => setOpenId(g.id)} />)}
          </ListCard>
        </React.Fragment>
      );
    });
  }

  return (
    <Box pb="8px">
      {list}
      <DesktopNoteCard />
      <GoalSheet
        goal={openGoal || lastGoal}
        isOpen={Boolean(openGoal)}
        onClose={() => setOpenId(null)}
        canUpdate={can('goal.update')}
        onStatusChange={handleStatusChange}
      />
    </Box>
  );
}
